import React, { useState, useEffect } from 'react';
import { Clock, Truck, MapPin, Sparkles, ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';

const HeroBanner = ({ branding }) => {
  const defaultName = "UPLANDS FOOD MARKET";
  const defaultTagline = "Your Neighborhood African & Caribbean Grocery Store";
  const defaultLocation = "MERIVALE ROAD";
  
  // Default banner images/videos - can be extended via branding
  const defaultBannerMedia = [
    { type: 'image', url: "https://base44.app/api/apps/6939fbb5c2c7684a1f6e4ebc/files/public/6939fbb5c2c7684a1f6e4ebc/0d15e8e76_IMG_0948.jpeg" },
  ];
  
  // Use branding banner_media if available, otherwise default
  const bannerMedia = branding?.banner_media?.length > 0 
    ? branding.banner_media 
    : (branding?.banner_url ? [{ type: 'image', url: branding.banner_url }] : defaultBannerMedia);
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Auto-advance carousel
  useEffect(() => {
    if (!isPlaying || bannerMedia.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % bannerMedia.length);
    }, 5000); // Change every 5 seconds
    
    return () => clearInterval(interval);
  }, [isPlaying, bannerMedia.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + bannerMedia.length) % bannerMedia.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % bannerMedia.length);
  };

  const currentMedia = bannerMedia[currentIndex];

  return (
    <div className="relative w-full h-32 md:h-40 lg:h-44 overflow-hidden bg-black shadow-lg">
      {/* Background Media Container */}
      <div className="absolute inset-0">
        {currentMedia?.type === 'video' ? (
          <video
            key={currentMedia.url}
            src={currentMedia.url}
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            onPlay={() => setIsVideoPlaying(true)}
            onPause={() => setIsVideoPlaying(false)}
          />
        ) : (
          <img
            key={currentMedia?.url}
            src={currentMedia?.url || defaultBannerMedia[0].url}
            alt={branding?.business_name || defaultName}
            className="w-full h-full object-cover transition-opacity duration-500"
            onError={(e) => {
              e.target.src = defaultBannerMedia[0].url;
            }}
          />
        )}
        
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/70" />
      </div>
      
      {/* Content - Always visible over rotating images */}
      <div className="relative h-full flex items-center justify-between px-4 md:px-8 lg:px-16">
        {/* Left Side - Store Info (Constant) */}
        <div className="flex-1 min-w-0">
          {/* Location Badge */}
          <div className="inline-flex items-center gap-1.5 bg-[#0a1628] rounded-full px-2 py-0.5 mb-1 shadow-lg">
            <MapPin className="w-3 h-3 text-white" />
            <span className="text-white text-xs font-bold uppercase tracking-wide">
              {defaultLocation}
            </span>
          </div>
          
          {/* Main Title - Constant */}
          <h1 className="text-white text-xl md:text-2xl lg:text-3xl font-extrabold tracking-wide leading-tight drop-shadow-lg truncate">
            {branding?.business_name || defaultName}
          </h1>
          
          {/* Tagline */}
          <p className="text-white/90 text-xs md:text-sm mt-0.5 font-semibold drop-shadow-md flex items-center gap-1.5 truncate">
            <Sparkles className="w-3 h-3 text-white flex-shrink-0" />
            <span className="truncate">{branding?.business_tagline || defaultTagline}</span>
          </p>
        </div>
        
        {/* Right Side - Info Badges */}
        <div className="hidden sm:flex flex-col gap-1.5 flex-shrink-0 ml-4">
          <div className="flex items-center gap-1.5 bg-[#0a1628]/90 rounded-full px-3 py-1 shadow-lg">
            <Clock className="w-3.5 h-3.5 text-white" />
            <span className="text-white text-xs font-bold">30-45 min</span>
          </div>
          <div className="flex items-center gap-1.5 bg-[#0a1628]/90 rounded-full px-3 py-1 shadow-lg">
            <Truck className="w-3.5 h-3.5 text-white" />
            <span className="text-white text-xs font-bold">FREE over $50</span>
          </div>
        </div>
      </div>
      
      {/* Carousel Controls - Only show if multiple media */}
      {bannerMedia.length > 1 && (
        <>
          {/* Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors z-10"
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors z-10"
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          
          {/* Dots Indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
            {bannerMedia.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-white w-4' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
            
            {/* Play/Pause Button */}
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="ml-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            </button>
          </div>
        </>
      )}
      
      {/* Mobile Info Badges */}
      <div className="sm:hidden absolute top-2 right-2 flex flex-col gap-1 z-10">
        <div className="flex items-center gap-1 bg-[#0a1628]/90 rounded-full px-2 py-0.5 shadow-lg">
          <Clock className="w-3 h-3 text-white" />
          <span className="text-white text-xs font-bold">30-45 min</span>
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;
